import type { DocumentNode } from "@apollo/client";
import { ELEMENT_TYPE, assetClassFilterArgs } from "@eaco/forms-core";

import type { EntityPickerRow, EntityRichEntry, PickerContextSpec } from "./EntityPicker";
import {
  DOCUMENT_ELEMENT_ASSETS_QUERY,
  DOCUMENT_ELEMENT_CONTACTS_QUERY,
  DOCUMENT_ELEMENT_CUSTOMERS_QUERY,
  DOCUMENT_ELEMENT_JOBS_QUERY,
  DOCUMENT_ELEMENT_MANAGED_PROPERTIES_QUERY,
  DOCUMENT_ELEMENT_PROJECTS_QUERY,
  DOCUMENT_ELEMENT_USERS_QUERY,
} from "./queries";

/**
 * One row per entity-data picker: the collection query it searches, the
 * top-level field on the response, and the projection from a fetched row
 * to the snapshot persisted in the answer. The pickers and fill-by-voice
 * share these so a voice pick is byte-for-byte what a tap would store.
 */
export interface EntityKind {
  typeId: string;
  /** Apollo document; `any` so graphql 15 and 16 consumers both fit. */
  query: DocumentNode | any;
  collectionField: string;
  rowToSnapshot: (row: EntityPickerRow) => EntityRichEntry;
  /** Row fields, besides `name`, that count as an exact spoken match. */
  matchKeys: string[];
  /** Short noun for prompts and copy. */
  noun: string;
  /** Auto-fill source and filterable context kinds, as the picker declares them. */
  contextSpec: PickerContextSpec;
}

export const managedPropertySnapshot = (row: EntityPickerRow): EntityRichEntry => ({
  id: row.id,
  name: row.name,
  addressFormatted: row.addressFormatted ?? null,
  refId: row.refId ?? null,
  customerName: row.customerName ?? null,
});

export const contactSnapshot = (row: EntityPickerRow): EntityRichEntry => ({
  id: row.id,
  name: row.name,
  email: row.email ?? null,
  mobile: row.mobile ?? null,
  businessName: row.businessName ?? null,
});

export const jobSnapshot = (row: EntityPickerRow): EntityRichEntry => ({
  id: row.id,
  name: row.name,
  refId: row.refId ?? null,
  status: row.status ?? null,
  propertyName: row.propertyName ?? null,
  customerName: row.customerName ?? null,
});

export const assetSnapshot = (row: EntityPickerRow): EntityRichEntry => ({
  id: row.id,
  name: row.name,
  refId: row.refId ?? null,
  propertyName: row.propertyName ?? null,
  customerName: row.customerName ?? null,
});

export const userSnapshot = (row: EntityPickerRow): EntityRichEntry => ({
  id: row.id,
  name: row.name,
  email: row.email ?? null,
  jobTitle: row.jobTitle ?? null,
  preferredName: row.preferredName ?? null,
});

export const projectSnapshot = (row: EntityPickerRow): EntityRichEntry => ({
  id: row.id,
  name: row.name,
  nameAbbr: row.nameAbbr ?? null,
  hexColor: row.hexColor ?? null,
  customerName: row.customerName ?? null,
});

export const customerSnapshot = (row: EntityPickerRow): EntityRichEntry => ({
  id: row.id,
  name: row.name,
});

export const ENTITY_KINDS: Record<string, EntityKind> = {
  [ELEMENT_TYPE.managedProperty]: {
    typeId: ELEMENT_TYPE.managedProperty,
    contextSpec: { autoFillFrom: "managedProperty", filterableBy: ["customer"] },
    query: DOCUMENT_ELEMENT_MANAGED_PROPERTIES_QUERY,
    collectionField: "documentElementManagedProperties",
    rowToSnapshot: managedPropertySnapshot,
    matchKeys: ["addressFormatted", "refId"],
    noun: "property",
  },
  [ELEMENT_TYPE.contact]: {
    typeId: ELEMENT_TYPE.contact,
    // No customer filter: ISM's contact picker has nothing to filter contacts
    // by customer on, and the designer no longer offers it.
    contextSpec: { autoFillFrom: "contact", filterableBy: [] },
    query: DOCUMENT_ELEMENT_CONTACTS_QUERY,
    collectionField: "documentElementContacts",
    rowToSnapshot: contactSnapshot,
    matchKeys: ["email", "businessName"],
    noun: "contact",
  },
  [ELEMENT_TYPE.job]: {
    typeId: ELEMENT_TYPE.job,
    contextSpec: { autoFillFrom: "job", filterableBy: ["customer", "managedProperty", "project"] },
    query: DOCUMENT_ELEMENT_JOBS_QUERY,
    collectionField: "documentElementJobs",
    rowToSnapshot: jobSnapshot,
    matchKeys: ["refId"],
    noun: "job",
  },
  [ELEMENT_TYPE.asset]: {
    typeId: ELEMENT_TYPE.asset,
    contextSpec: {
      autoFillFrom: "asset",
      filterableBy: ["customer", "managedProperty"],
      // The element's "Filter by asset class" setting.
      designFilters: assetClassFilterArgs,
    },
    query: DOCUMENT_ELEMENT_ASSETS_QUERY,
    collectionField: "documentElementAssets",
    rowToSnapshot: assetSnapshot,
    matchKeys: ["refId"],
    noun: "asset",
  },
  [ELEMENT_TYPE.user]: {
    typeId: ELEMENT_TYPE.user,
    contextSpec: { autoFillFrom: "user", filterableBy: [] },
    query: DOCUMENT_ELEMENT_USERS_QUERY,
    collectionField: "documentElementUsers",
    rowToSnapshot: userSnapshot,
    matchKeys: ["preferredName", "email"],
    noun: "person",
  },
  [ELEMENT_TYPE.project]: {
    typeId: ELEMENT_TYPE.project,
    contextSpec: { autoFillFrom: "project", filterableBy: ["customer"] },
    query: DOCUMENT_ELEMENT_PROJECTS_QUERY,
    collectionField: "documentElementProjects",
    rowToSnapshot: projectSnapshot,
    matchKeys: ["nameAbbr"],
    noun: "project",
  },
  [ELEMENT_TYPE.customer]: {
    typeId: ELEMENT_TYPE.customer,
    contextSpec: { autoFillFrom: "customer", filterableBy: [] },
    query: DOCUMENT_ELEMENT_CUSTOMERS_QUERY,
    collectionField: "documentElementCustomers",
    rowToSnapshot: customerSnapshot,
    matchKeys: [],
    noun: "customer",
  },
};

/** A one-line description of a row for the review sheet. */
export function describeEntityRow(typeId: string, row: EntityPickerRow): string {
  const extra =
    typeId === ELEMENT_TYPE.managedProperty
      ? row.addressFormatted
      : typeId === ELEMENT_TYPE.contact
      ? row.businessName ?? row.email
      : typeId === ELEMENT_TYPE.job || typeId === ELEMENT_TYPE.asset
      ? [row.refId, row.propertyName].filter(Boolean).join(" · ")
      : typeId === ELEMENT_TYPE.user
      ? row.jobTitle ?? row.email
      : typeId === ELEMENT_TYPE.project
      ? row.customerName
      : null;
  const s = typeof extra === "string" && extra.trim() ? extra.trim() : "";
  return s ? `${row.name} (${s})` : row.name;
}
